import React, { useState, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Slide from '@material-ui/core/Slide'
import Button from '@material-ui/core/Button'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import FormGroup from '@material-ui/core/FormGroup'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Clear from '@material-ui/icons/Clear'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'
import componentStylesDialog from 'assets/theme/components/dialog.js'
import componentStylesForms from 'assets/theme/components/forms.js'
import componentStylesButtons from 'assets/theme/components/button.js'

const useStylesDialog = makeStyles(componentStylesDialog)
const useStylesForms = makeStyles(componentStylesForms)
const useStylesButtons = makeStyles(componentStylesButtons)

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction='down' ref={ref} {...props} />
})

export const PROJECTS = ['PPL', 'NPH', 'CI', 'EE', 'H', 'ES', 'Other']
export const TYPES = ['Wages', 'Contractor', 'Operating']
export const CATEGORIES = [
    'Content Writing',
    'Acting',
    'Editing',
    'Graphic Design',
    'Development',
    'Paid Links',
    'PR',
    'Social Media',
    'SEO',
    'Ads Management',
    'Bonuses',
    'Sales',
    'Other'
]
export const OPERATING_CATEGORIES = ['Paid Links', 'PR', 'Social Media', 'SEO', 'Ads Management', 'Bonuses']
export const WAGES_CATEGORIES = ['Sales']
export const PAYERS = ['BQ', 'SS', 'H']

function emptySplit() {
    return PROJECTS.reduce((acc, p) => {
        acc[p] = 0
        return acc
    }, {})
}

export default function ExpenseModal(props) {
    const classes = { ...useStylesDialog(), ...useStylesForms(), ...useStylesButtons() }
    const api = new AdminAPI()

    const isEdit = !!props.expense
    const knownEntries = props.knownEntries || [] // for autocomplete + last-split lookup

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [type, setType] = useState('Contractor')
    const [category, setCategory] = useState('Content Writing')
    const [payer, setPayer] = useState('BQ')
    const [amount, setAmount] = useState('')
    const [notes, setNotes] = useState('')
    const [split, setSplit] = useState(emptySplit())
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (props.expense) {
            const e = props.expense
            setName(e.name || '')
            setDescription(e.description || '')
            setType(e.type || 'Contractor')
            setCategory(e.category || 'Content Writing')
            setPayer(e.payer || 'BQ')
            setAmount(e.amount != null ? String(e.amount) : '')
            setNotes(e.notes || '')
            setSplit({ ...emptySplit(), ...(e.split || {}) })
        } else {
            setName('')
            setDescription('')
            setType('Contractor')
            setCategory('Content Writing')
            setPayer('BQ')
            setAmount('')
            setNotes('')
            setSplit(emptySplit())
        }
    }, [props.expense, props.isOpen])

    const nameOptions = useMemo(() => {
        const set = new Set()
        knownEntries.forEach((e) => e.name && set.add(e.name))
        return Array.from(set).sort()
    }, [knownEntries])

    function applyNameDefaults(newName) {
        if (isEdit) return
        if (!newName) return
        // find most recent entry for this name
        const matches = knownEntries
            .filter((e) => e.name === newName)
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        if (matches.length > 0) {
            const last = matches[0]
            if (last.type) setType(last.type)
            if (last.category) setCategory(last.category)
            if (last.payer) setPayer(last.payer)
            if (last.split) setSplit({ ...emptySplit(), ...last.split })
        }
    }

    function handleCategoryChange(newCategory) {
        setCategory(newCategory)
        // auto-set type based on category
        if (OPERATING_CATEGORIES.includes(newCategory)) {
            setType('Operating')
        } else if (WAGES_CATEGORIES.includes(newCategory)) {
            setType('Wages')
        } else if (type === 'Operating' || type === 'Wages') {
            setType('Contractor')
        }
    }

    function setSplitValue(project, value) {
        const num = value === '' ? 0 : parseFloat(value)
        setSplit({ ...split, [project]: isNaN(num) ? 0 : num })
    }

    function evenSplit() {
        const v = Math.round((100 / PROJECTS.length) * 100) / 100
        const obj = {}
        let running = 0
        PROJECTS.forEach((p, i) => {
            if (i === PROJECTS.length - 1) {
                obj[p] = Math.round((100 - running) * 100) / 100
            } else {
                obj[p] = v
                running += v
            }
        })
        setSplit(obj)
    }

    function allToOne(project) {
        const obj = emptySplit()
        obj[project] = 100
        setSplit(obj)
    }

    const splitTotal = useMemo(() => {
        return Math.round(PROJECTS.reduce((sum, p) => sum + (parseFloat(split[p]) || 0), 0) * 100) / 100
    }, [split])

    const splitValid = splitTotal === 100
    const amountValid = amount !== '' && !isNaN(parseFloat(amount)) && parseFloat(amount) >= 0
    const nameValid = name.trim().length > 0
    const canSubmit = nameValid && amountValid && splitValid && !saving

    async function save() {
        if (!canSubmit) return
        setSaving(true)
        try {
            const payload = {
                month: props.month,
                name: name.trim(),
                description,
                type,
                category,
                payer,
                amount: parseFloat(amount),
                split,
                notes
            }
            if (isEdit) {
                payload.id = props.expense.id
                const updated = await api.updateExpense(payload)
                props.onSaved && props.onSaved(updated, 'update')
            } else {
                const created = await api.addExpense(payload)
                props.onSaved && props.onSaved(created, 'add')
            }
            props.close()
        } catch (e) {
            onError(e)
            NotificationManager.error('Could not save expense')
        } finally {
            setSaving(false)
        }
    }

    async function handleDelete() {
        if (!isEdit || !props.expense) return
        const confirmed = window.confirm(`Delete this expense?\n\n${props.expense.name} — €${props.expense.amount}`)
        if (!confirmed) return
        setSaving(true)
        try {
            await api.deleteExpense(props.expense.month, props.expense.id)
            props.onSaved && props.onSaved(props.expense, 'delete')
            props.close()
        } catch (e) {
            onError(e)
            NotificationManager.error('Could not delete expense')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog
            open={props.isOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={() => props.close()}
            maxWidth='md'
            fullWidth
        >
            <div className={classes.dialogHeader}>
                <Typography variant='h5' component='h5' className={classes.dialogTitle}>
                    {isEdit ? 'Edit Expense' : 'Add Expense'} — {props.month}
                </Typography>
                <IconButton onClick={() => props.close()}>
                    <Clear />
                </IconButton>
            </div>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>Name</FormLabel>
                            <OutlinedInput
                                fullWidth
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onBlur={(e) => applyNameDefaults(e.target.value)}
                                inputProps={{ list: 'expense-name-options' }}
                            />
                            <datalist id='expense-name-options'>
                                {nameOptions.map((n) => (
                                    <option key={n} value={n} />
                                ))}
                            </datalist>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>Description</FormLabel>
                            <OutlinedInput
                                fullWidth
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>Type</FormLabel>
                            <FormControl variant='outlined' fullWidth>
                                <Select value={type} onChange={(e) => setType(e.target.value)}>
                                    {TYPES.map((t) => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>Category</FormLabel>
                            <FormControl variant='outlined' fullWidth>
                                <Select value={category} onChange={(e) => handleCategoryChange(e.target.value)}>
                                    {CATEGORIES.map((c) => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>Payer</FormLabel>
                            <FormControl variant='outlined' fullWidth>
                                <Select value={payer} onChange={(e) => setPayer(e.target.value)}>
                                    {PAYERS.map((p) => (
                                        <MenuItem key={p} value={p}>{p}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>Amount (€)</FormLabel>
                            <OutlinedInput
                                fullWidth
                                type='number'
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                inputProps={{ min: 0, step: '0.01' }}
                            />
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display='flex' alignItems='center' justifyContent='space-between' marginBottom='0.5rem'>
                            <FormLabel>Project Split (%)</FormLabel>
                            <Box>
                                <Button size='small' onClick={() => evenSplit()}>Even</Button>
                                <Button size='small' onClick={() => setSplit(emptySplit())}>Clear</Button>
                                <Box
                                    component='span'
                                    marginLeft='1rem'
                                    fontWeight='bold'
                                    color={splitValid ? '#2dce89' : '#f5365c'}
                                >
                                    Total: {splitTotal}%
                                </Box>
                            </Box>
                        </Box>
                        <Grid container spacing={1}>
                            {PROJECTS.map((p) => (
                                <Grid item xs={6} sm={4} md={2} key={p}>
                                    <FormLabel style={{ fontSize: '0.75rem' }}>{p}</FormLabel>
                                    <OutlinedInput
                                        fullWidth
                                        type='number'
                                        value={split[p]}
                                        onChange={(e) => setSplitValue(p, e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                                        endAdornment={
                                            <Button
                                                size='small'
                                                style={{ minWidth: 0, padding: '0 4px', fontSize: '0.65rem' }}
                                                onClick={() => allToOne(p)}
                                                title={`Set 100% to ${p}`}
                                            >
                                                100
                                            </Button>
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Notes</FormLabel>
                            <OutlinedInput
                                fullWidth
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                multiline
                                minRows={2}
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Box width='100%' display='flex' justifyContent='space-between' alignItems='center'>
                    <Box>
                        {isEdit && (
                            <Button
                                onClick={() => handleDelete()}
                                style={{ color: '#f5365c' }}
                                disabled={saving}
                            >
                                Delete
                            </Button>
                        )}
                    </Box>
                    <Box>
                        <Button onClick={() => props.close()} style={{ marginRight: '0.5rem' }}>Cancel</Button>
                        <Button
                            color='primary'
                            variant='contained'
                            onClick={() => save()}
                            disabled={!canSubmit}
                        >
                            {isEdit ? 'Save' : 'Add'}
                        </Button>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
